import React, { useCallback, useEffect, useMemo, useState } from "react";
import { normalizeArray } from "../../apiClient";
import {
  listOfferings,
  createOffering,
  updateOffering,
  listServiceTypes,
} from "../../api/serviceCatalogApi";
import "./ServiceCatalog.css";

const money = (v) =>
  v === null || v === undefined || v === "" ? "" : `$${Number(v).toFixed(2)}`;

const blankDraft = () => ({
  name: "",
  service_type: "",
  default_duration_minutes: "",
  base_price: "",
  is_recurring: false,
});

/**
 * Service Catalog — offerings are the templates a plan or visit inherits from.
 * The screen's job is not just to list; it teaches. An owner must (1) mark
 * offerings recurring before any plan or auto-generated visit is possible, and
 * (2) assign a service type before a booking can prefill. Both are surfaced up
 * front so no owner discovers a gap at booking time.
 */
export default function ServiceCatalog() {
  const [offerings, setOfferings] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null); // offering id, or "new"
  const [draft, setDraft] = useState(blankDraft());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [offRes, stRes] = await Promise.all([
        listOfferings({ ordering: "name" }),
        listServiceTypes(),
      ]);
      setOfferings(normalizeArray(offRes.data));
      setServiceTypes(normalizeArray(stRes.data));
    } catch (e) {
      setError("Couldn't load the service catalog. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const recurringCount = useMemo(
    () => offerings.filter((o) => o.is_recurring).length,
    [offerings]
  );
  const missingTypeCount = useMemo(
    () => offerings.filter((o) => !o.service_type).length,
    [offerings]
  );

  const stName = useCallback(
    (id) => serviceTypes.find((s) => String(s.id) === String(id))?.name || "",
    [serviceTypes]
  );

  const beginEdit = (o) => {
    setEditingId(o.id);
    setDraft({
      name: o.name || "",
      service_type: o.service_type || "",
      default_duration_minutes: o.default_duration_minutes ?? "",
      base_price: o.base_price ?? "",
      is_recurring: Boolean(o.is_recurring),
    });
  };
  const beginAdd = () => {
    setEditingId("new");
    setDraft(blankDraft());
  };
  const cancel = () => {
    setEditingId(null);
    setDraft(blankDraft());
  };

  const save = async () => {
    if (!draft.name.trim()) {
      setError("An offering needs a name.");
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      name: draft.name.trim(),
      service_type: draft.service_type || null,
      default_duration_minutes:
        draft.default_duration_minutes === "" ? null : Number(draft.default_duration_minutes),
      base_price: draft.base_price === "" ? null : Number(draft.base_price),
      is_recurring: Boolean(draft.is_recurring),
    };
    try {
      if (editingId === "new") await createOffering(payload);
      else await updateOffering(editingId, payload);
      cancel();
      await load();
    } catch (e) {
      setError("Couldn't save. Check the fields and try again.");
    } finally {
      setSaving(false);
    }
  };

  // Quick inline recurring toggle from a row (no full edit).
  const toggleRecurring = async (o) => {
    try {
      await updateOffering(o.id, { is_recurring: !o.is_recurring });
      await load();
    } catch (e) {
      setError("Couldn't update that offering.");
    }
  };

  return (
    <div className="svc-catalog">
      <header className="svc-catalog__head">
        <p className="svc-catalog__sub">
          Mark a service <strong>recurring</strong> to enable plans; assign a
          <strong> service type</strong> so bookings prefill its length.
        </p>
        <button className="svc-btn svc-btn--primary" onClick={beginAdd} disabled={editingId === "new"}>
          Add service
        </button>
      </header>

      {/* Teaching banners — an incomplete catalog explains itself. */}
      {!loading && offerings.length > 0 && recurringCount === 0 && (
        <div className="svc-note svc-note--warn">
          <strong>No recurring services yet.</strong> Plans and auto-generated visits
          are unavailable until you mark at least one service recurring. Toggle
          “Recurring” on any service that repeats — lawn care, pest control, a
          quarterly plan.
        </div>
      )}
      {!loading && missingTypeCount > 0 && (
        <div className="svc-note">
          <strong>
            {missingTypeCount} {missingTypeCount === 1 ? "service has" : "services have"} no
            service type.
          </strong>{" "}
          Those won’t prefill a service type or duration when you book — assign one to
          each flagged row below.
        </div>
      )}
      {error && <div className="svc-note svc-note--error">{error}</div>}

      {loading ? (
        <div className="svc-empty">Loading…</div>
      ) : offerings.length === 0 && editingId !== "new" ? (
        <div className="svc-empty">
          No services yet. <button className="svc-link" onClick={beginAdd}>Add your first one.</button>
        </div>
      ) : (
        <div className="svc-list">
          {editingId === "new" && (
            <OfferingEditor
              draft={draft}
              setDraft={setDraft}
              serviceTypes={serviceTypes}
              onSave={save}
              onCancel={cancel}
              saving={saving}
              isNew
            />
          )}
          {offerings.map((o) =>
            editingId === o.id ? (
              <OfferingEditor
                key={o.id}
                draft={draft}
                setDraft={setDraft}
                serviceTypes={serviceTypes}
                onSave={save}
                onCancel={cancel}
                saving={saving}
              />
            ) : (
              <div className="svc-row" key={o.id}>
                <div className="svc-row__main">
                  <div className="svc-row__name">
                    {o.name}
                    <span className={`svc-tag ${o.is_recurring ? "svc-tag--rec" : "svc-tag--one"}`}>
                      {o.is_recurring ? "Recurring" : "One-time"}
                    </span>
                  </div>
                  <div className="svc-row__meta">
                    {o.service_type ? (
                      <span>{o.service_type_name || stName(o.service_type)}</span>
                    ) : (
                      <span className="svc-row__flag">No service type — won’t prefill</span>
                    )}
                    {o.default_duration_minutes ? <span>· {o.default_duration_minutes} min</span> : null}
                    {o.base_price !== null && o.base_price !== undefined ? (
                      <span>· {money(o.base_price)}</span>
                    ) : null}
                  </div>
                </div>
                <div className="svc-row__actions">
                  <button className="svc-btn svc-btn--ghost" onClick={() => toggleRecurring(o)}>
                    {o.is_recurring ? "Make one-time" : "Make recurring"}
                  </button>
                  <button className="svc-btn" onClick={() => beginEdit(o)}>Edit</button>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

function OfferingEditor({ draft, setDraft, serviceTypes, onSave, onCancel, saving, isNew }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return (
    <div className="svc-row svc-row--edit">
      <div className="svc-edit__grid">
        <label className="svc-field svc-field--wide">
          <span>Name</span>
          <input value={draft.name} onChange={(e) => set("name", e.target.value)} autoFocus placeholder="e.g. Quarterly Pest Control" />
        </label>
        <label className="svc-field">
          <span>Service type</span>
          <select value={draft.service_type || ""} onChange={(e) => set("service_type", e.target.value)}>
            <option value="">— none (won’t prefill)</option>
            {serviceTypes.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </label>
        <label className="svc-field">
          <span>Default duration (min)</span>
          <input type="number" min="0" value={draft.default_duration_minutes} onChange={(e) => set("default_duration_minutes", e.target.value)} placeholder="60" />
        </label>
        <label className="svc-field">
          <span>Price</span>
          <input type="number" min="0" step="0.01" value={draft.base_price} onChange={(e) => set("base_price", e.target.value)} placeholder="120.00" />
        </label>
        <label className="svc-field svc-field--check">
          <input type="checkbox" checked={draft.is_recurring} onChange={(e) => set("is_recurring", e.target.checked)} />
          <span>Recurring — enables plans &amp; auto-generated visits</span>
        </label>
      </div>
      <div className="svc-edit__actions">
        <button className="svc-btn svc-btn--ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="svc-btn svc-btn--primary" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : isNew ? "Add offering" : "Save"}
        </button>
      </div>
    </div>
  );
}
